import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';

import { ApiError, request } from '@/api/client';
import type { Metric, RecomputeStatus } from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, type Column } from '@/components/DataTable';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  ErrorState,
  Field,
  Modal,
  TableSkeleton,
  TextInput,
} from '@/components/ui';
import { decimal } from '@/lib/format';
import { useResource } from '@/lib/hooks';

export function ScoringPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState<Metric | null>(null);
  const [job, setJob] = useState<RecomputeStatus | null>(null);
  const language = i18n.language;

  const { data, error, loading, reload } = useResource<Metric[]>(
    '/api/v1/admin/scoring/metrics',
    undefined,
    [],
  );

  const running = job?.status === 'running';
  const pollTimer = useRef<number | null>(null);

  useEffect(() => {
    void request<RecomputeStatus>('/api/v1/admin/scoring/recompute').then(setJob).catch(() => {
      /* Not knowing the last run is survivable; the page still works. */
    });
  }, []);

  useEffect(() => {
    // Polls only while a job is running, and stops the moment it is not --
    // an admin who leaves this tab open should not generate a request a
    // second all afternoon.
    if (!running) {
      if (pollTimer.current) window.clearInterval(pollTimer.current);
      return;
    }
    pollTimer.current = window.setInterval(() => {
      void request<RecomputeStatus>('/api/v1/admin/scoring/recompute')
        .then(setJob)
        .catch(() => undefined);
    }, 2000);
    return () => {
      if (pollTimer.current) window.clearInterval(pollTimer.current);
    };
  }, [running]);

  async function startRecompute() {
    try {
      setJob(
        await request<RecomputeStatus>('/api/v1/admin/scoring/recompute', {
          method: 'POST',
        }),
      );
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  async function reset(metric: Metric) {
    try {
      await request(`/api/v1/admin/scoring/metrics/${metric.code}/reset`, {
        method: 'POST',
      });
      toast.success(t('scoring.resetDone'));
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  const metrics = (data ?? []).filter((metric) => !activeOnly || metric.is_active);
  const changed = (data ?? []).filter((metric) => metric.is_overridden).length;

  const columns: Column<Metric>[] = [
    {
      key: 'metric',
      header: t('scoring.metric'),
      render: (metric) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-medium text-ink">{metric.skill_name}</span>
            {metric.is_overridden ? (
              <Badge tone="caution">{t('scoring.changed')}</Badge>
            ) : null}
            {!metric.is_active ? (
              <Badge tone="neutral" title={t('scoring.parkedHint')}>
                {t('scoring.parked')}
              </Badge>
            ) : null}
          </div>
          <div className="truncate text-2xs text-ink-subtle">
            {metric.category} · {t(`scoring.logic.${metric.logic}`)}
          </div>
        </div>
      ),
    },
    {
      key: 'unit',
      header: t('scoring.unit'),
      render: (metric) => <span className="text-ink-muted">{metric.unit}</span>,
    },
    {
      key: 'baseline',
      header: t('scoring.baselineFull'),
      align: 'right',
      render: (metric) => (
        <ValueCell
          current={metric.baseline_100}
          shipped={metric.default_baseline_100}
          language={language}
          shippedLabel={t('scoring.shipped')}
        />
      ),
    },
    {
      key: 'elite',
      header: t('scoring.eliteFull'),
      align: 'right',
      render: (metric) => (
        <ValueCell
          current={metric.elite_1000}
          shipped={metric.default_elite_1000}
          language={language}
          shippedLabel={t('scoring.shipped')}
        />
      ),
    },
    {
      key: 'curve',
      header: t('scoring.curve'),
      align: 'right',
      render: (metric) => (
        <ValueCell
          current={metric.curve_factor}
          shipped={metric.default_curve_factor}
          language={language}
          shippedLabel={t('scoring.shipped')}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.edit'),
      align: 'right',
      render: (metric) => (
        <div className="flex justify-end gap-1">
          <Button className="text-xs" onClick={() => setEditing(metric)}>
            {t('scoring.edit')}
          </Button>
          {metric.is_overridden ? (
            <Button
              className="text-xs"
              icon={<RotateCcw className="h-3.5 w-3.5" />}
              onClick={() => reset(metric)}
              aria-label={t('scoring.resetMetric')}
              title={t('scoring.resetMetric')}
            />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('scoring.title')} subtitle={t('scoring.subtitle')} />

      <section className="card mb-4 p-4">
        <h2 className="text-sm font-semibold text-ink">{t('scoring.recomputeTitle')}</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">
          {t('scoring.recomputeBody')}
        </p>

        {changed > 0 ? (
          <p className="mt-2 flex items-start gap-1.5 text-sm text-amber-800 dark:text-amber-200">
            <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            {t('scoring.pendingChanges', { count: changed })}
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            icon={<RefreshCw className="h-4 w-4" />}
            loading={running}
            onClick={startRecompute}
          >
            {t('scoring.recomputeStart')}
          </Button>

          <p aria-live="polite" className="text-xs text-ink-subtle">
            {job?.status === 'running'
              ? t('scoring.recomputeRunning', {
                  processed: job.processed,
                  total: job.total,
                })
              : job?.status === 'done'
                ? t('scoring.recomputeDone', {
                    processed: job.processed,
                    pillars: job.pillars_touched,
                  })
                : job?.status === 'failed'
                  ? t('scoring.recomputeFailed', { error: job.error ?? '' })
                  : t('scoring.recomputeIdle')}
          </p>
        </div>

        {running && job.total > 0 ? (
          <div
            className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-sunken"
            role="progressbar"
            // A progressbar with no name announces as "progress bar, 40%" and
            // nothing else; the reader has no idea what is progressing.
            aria-label={t('scoring.recomputeTitle')}
            aria-valuemin={0}
            aria-valuemax={job.total}
            aria-valuenow={job.processed}
          >
            <div
              className="h-full rounded-full bg-brand transition-[width]"
              style={{ width: `${Math.round((job.processed / job.total) * 100)}%` }}
            />
          </div>
        ) : null}
      </section>

      <div className="card">
        <div className="flex items-center justify-between gap-3 border-b border-line p-3">
          <label className="flex min-h-[2.25rem] items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line text-brand"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
            />
            {t('scoring.activeOnly')}
          </label>
          <span className="text-xs text-ink-subtle tabular">{metrics.length}</span>
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : loading && !data ? (
          <TableSkeleton columns={6} />
        ) : (
          <DataTable
            caption={t('scoring.title')}
            columns={columns}
            rows={metrics}
            rowKey={(metric) => metric.code}
          />
        )}
      </div>

      {editing ? (
        <EditMetric
          metric={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t('scoring.saved'));
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function ValueCell({
  current,
  shipped,
  language,
  shippedLabel,
}: {
  current: number;
  shipped: number;
  language: string;
  shippedLabel: string;
}) {
  const changed = current !== shipped;
  return (
    <div>
      <div className="font-medium text-ink">{decimal(current, language)}</div>
      {/* The shipped value stays visible next to the tuned one, so the size of
          a change is readable without opening the dialog. */}
      {changed ? (
        <div className="text-2xs text-ink-subtle">
          {shippedLabel}: {decimal(shipped, language)}
        </div>
      ) : null}
    </div>
  );
}

function EditMetric({
  metric,
  onClose,
  onSaved,
}: {
  metric: Metric;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [baseline, setBaseline] = useState(String(metric.baseline_100));
  const [elite, setElite] = useState(String(metric.elite_1000));
  const [curve, setCurve] = useState(String(metric.curve_factor));
  const [busy, setBusy] = useState(false);

  const baselineValue = Number(baseline);
  const eliteValue = Number(elite);
  const curveValue = Number(curve);

  const numbersOk =
    Number.isFinite(baselineValue) &&
    Number.isFinite(eliteValue) &&
    Number.isFinite(curveValue);
  const curveOk = curveValue >= 0.1 && curveValue <= 10;
  const spanOk = baselineValue !== eliteValue;

  async function save() {
    setBusy(true);
    try {
      await request(`/api/v1/admin/scoring/metrics/${metric.code}`, {
        method: 'PUT',
        body: {
          baseline_100: baselineValue,
          elite_1000: eliteValue,
          curve_factor: curveValue,
        },
      });
      onSaved();
    } catch (caught) {
      toast.failure(
        caught instanceof ApiError ? caught.message : t('common.loadFailed'),
      );
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('scoring.editTitle', { name: metric.skill_name })}
      description={`${metric.category} · ${t(`scoring.logic.${metric.logic}`)} · ${metric.unit}`}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!numbersOk || !curveOk || !spanOk}
            onClick={save}
          >
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('scoring.baselineFull')}
          hint={`${t('scoring.shipped')}: ${metric.default_baseline_100}`}
          error={!spanOk && numbersOk ? t('scoring.sameValues') : undefined}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="decimal"
              step="any"
              value={baseline}
              invalid={!spanOk && numbersOk}
              onChange={(event) => setBaseline(event.target.value)}
            />
          )}
        </Field>

        <Field
          label={t('scoring.eliteFull')}
          hint={`${t('scoring.shipped')}: ${metric.default_elite_1000}`}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="decimal"
              step="any"
              value={elite}
              invalid={!spanOk && numbersOk}
              onChange={(event) => setElite(event.target.value)}
            />
          )}
        </Field>

        <Field
          label={t('scoring.curve')}
          hint={`${t('scoring.shipped')}: ${metric.default_curve_factor}`}
          error={!curveOk && numbersOk ? t('errors.curveRange') : undefined}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={0.1}
              max={10}
              value={curve}
              invalid={!curveOk && numbersOk}
              onChange={(event) => setCurve(event.target.value)}
            />
          )}
        </Field>

        <p className="rounded-lg bg-surface-sunken px-3 py-2 text-xs text-ink-muted">
          {t('scoring.recomputeBody')}
        </p>
      </div>
    </Modal>
  );
}
